import {isEqual} from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';
import NumberFormat from 'react-number-format';
import MediaQuery from 'react-responsive';
import {CSSTransition} from 'react-transition-group';
import {compose} from 'redux';
import FiltersContentLoader from '../../components/FiltersContentLoader';
import GridHistoryWithFilters from '../../components/GridHistoryWithFilters';
import HorizontalLoader from '../../components/HorizontalLoader';
import ListSearch from '../../components/ListSearch';
import ListSearchContentLoader from '../../components/ListSearchContentLoader';
import NoEventsMask from '../../components/NoEventsMask';
import QuickFilters from '../../components/QuickFilters';
import SetupEmailDropdown from '../../components/SetupEmailDropdown';
import {ANIMATION_TIMEOUT_APPEAR} from '../../constants/animation';
import ENDPOINT from '../../constants/endpoints';
import ERROR_CODE from '../../constants/error-codes';
import {MESSAGE_TYPE} from '../../constants/message-types';
import POPUP_STYLES from '../../constants/popup-styles';
import ROUTE from '../../constants/route';
import FileHelper from '../../helpers/file';
import {updateHistory} from '../../helpers/grid-history';
import {Permission, Role} from '../../helpers/user';
import withSearchActions from '../../hocs/withSearchActions';
import withTakeActions from '../../hocs/withTakeActions';
import useApi from '../../hooks/useApi';
import {useGrid} from '../../hooks/useGrid';
import {useGridFilters} from '../../hooks/useGridFilters';
import {useSelection} from '../../hooks/useSelection';
import PageLayout from '../../layouts/PageLayout';
import BasicDialog from '../../scenes/Dialog/components/BasicDialog';
import MassSendJobDialog from '../../scenes/Dialog/components/MassSendJobDialog';
import ResponseErrorDialog from '../../scenes/Dialog/components/ResponseErrorDialog';
import FiltersAccordion from '../../scenes/FiltersAccordion';
import FiltersWidget from '../../scenes/FiltersAccordion/components/FiltersWidget';
import List from '../../scenes/List';
import CandidateItem from '../../scenes/List/components/CandidateItem';
import Sidebar from '../../scenes/Sidebar';
import FiltersMobileToggler from '../../scenes/Sidebar/components/FiltersMobileToggler';
import WidgetOnMobile from '../../scenes/Sidebar/components/WidgetOnMobile';
import {get} from '../../services/api';
import './styles.css';

const ViewApplicants = props => {
  const {
    user,
    history,
    match: {
      params: { id, type },
    },
    searchAction: {
      addToShortlist,
      messageSelected,
      message,
      sendLinksEmail,
      sendScheduleEmail,
      sendGatEmail,
      downloadResumeApplicants,
      downloadExcelApplicants,
      setMassStatus,
    },
    takeAction,
    globalActions: { loaderShow, loaderHide },
  } = props;

  const api = useApi();

  const [emailSetupReqValue, setEmailSetupReqValue] = useState();
  const [isSendJobButtonDisabled, setSendJobButtonDisabled] = useState(false);

  const [appData, setAppData] = useState({
    PositionTitle: '',
    ApplicantsCount: null,
    CoverLetterRequire: false,
    IsSubscriptionProcess: false,
    HasSetupEmail: false,
    SetupEmailReqOptions: null,
  });

  const filters = useGridFilters(
    ENDPOINT.FILTERS.PROCESS_APPLICANTS,
    () => {
      setEmailSetupReqValue(null)
    },
    async () => {
      const response = await api.postRequest(ENDPOINT.FILTERS.PROCESS_APPLICANTS_CLEAR, { id });
      return response.data;
    },
    async () => {
      const response = await api.postRequest(ENDPOINT.FILTERS.PROCESS_APPLICANTS_RESET, { id });
      return response.data;
    }
  );

  const grid = useGrid(ENDPOINT.COLLECTION.PROCESS_APPLICANTS, params => {
    updateHistory(history, params);
  });

  const [
    { isSelected, selectAll, deselectAll, select, deselect, getSelectedIds },
  ] = useSelection(grid.data.ids);

  const [mobileFilters, toggleMobileFilters] = useState(false);
  const [quickFilters, setQuickFilters] = useState([
    {
      name: 'qf-organic',
      title: 'Organic Applicants',
      tooltip: 'Organic Applicants - applicants who applied themselves',
    },
  ]);

  useEffect(() => {
    api.get(
      ENDPOINT.ENTITY.PROCESS_APPLICANTS,
      { id },
      {
        handleError: () => history.replace(ROUTE.ERROR_404),
        handleSuccess: data => setAppData(data),
      }
    );
  }, []);

  useEffect(() => {
    if (emailSetupReqValue && emailSetupReqValue.filters) {
      filters.actions.setFilters(emailSetupReqValue.filters);
    }
  }, [emailSetupReqValue]);

  useEffect(() => {
    if (grid && grid.data.isFetching) {
      const nextQuickFilters = grid.data.params.hasOwnProperty('quickFilters')
        ? grid.data.params.quickFilters
        : [];

      const prevQuickFilters = quickFilters
        .filter(f => f.checked)
        .map(f => f.name);

      if (!isEqual(nextQuickFilters, prevQuickFilters)) {
        setQuickFilters(quickFilters =>
          quickFilters.map(f => ({
            ...f,
            checked: nextQuickFilters.includes(f.name),
          }))
        );
      }
    }
  }, [grid.data.isFetching]);

  const [
    isRecruiterPremiumRecFirm,
    isRecruiterGbs,
    isAllowedCandidatesShortlists,
    isAllowedPersonalCalendar,
    isAllowedChangeMultipleCandidatesStatus,
    isAllowedUseQuickFilters,
  ] = useMemo(
    () => [
      Role.isRecruiterPremiumRecFirm(user),
      Role.isRecruiterGbs(user),
      Permission.isAllowedCandidatesShortlists(user.permissions),
      Permission.isAllowedPersonalCalendar(user.permissions),
      Permission.isAllowedChangeMultipleCandidatesStatus(user.permissions),
      Permission.isAllowedUseQuickFilters(user.permissions),
    ],
    [user]
  );

  const handleQuickFiltersChange = (name, checked) => {
    const prevQuickFilters = quickFilters.map(f =>
      f.name === name ? { ...f, checked } : f
    );

    grid.actions.fetch({
      ...grid.data.params,
      quickFilters: prevQuickFilters.filter(f => f.checked).map(f => f.name),
    });
  };

  const toggleMobileFiltersHandler = () => {
    toggleMobileFilters(!mobileFilters);
  };

  const filtersAccordion = useMemo(() => {
    return (
      filters.data && (
        <FiltersAccordion
          title={'Filters'}
          subTitle={'Use the search Filters to narrow your candidate search.'}
          filters={filters.data}
          onChange={filters.actions.change}
          onGroupSelectAll={filters.actions.groupSelect}
          onGroupClearAll={filters.actions.groupClear}
          onClearAll={filters.actions.clear}
          onSearch={filters.actions.search}
          onPushValue={filters.actions.push}
          onResetToDefault={filters.actions.resetToDefault}
          render={renderProps => (
            <FiltersWidget
              topWidget={
                <>
                  {isAllowedUseQuickFilters && (
                    <QuickFilters
                      filters={quickFilters}
                      onChangeHandler={handleQuickFiltersChange}
                    />
                  )}
                  {appData.SetupEmailReqOptions ? (
                    <SetupEmailDropdown
                      value={emailSetupReqValue}
                      options={appData.SetupEmailReqOptions}
                      controlFunc={value => setEmailSetupReqValue(value)}
                    />
                  ) : null}
                </>
              }
              {...renderProps}
            />
          )}
        />
      )
    );
  }, [
    grid.data.params,
    filters.data,
    appData.SetupEmailReqOptions,
    emailSetupReqValue,
    quickFilters,
  ]);

  const sortControls = useMemo(() => {
    return [
      ...[
        {
          defaultCaption: 'Apply Date',
          toggledCaption: 'Apply Date',
          value: 'default',
        },
        {
          defaultCaption: 'Interest Date',
          toggledCaption: 'Interest Date',
          value: 'confirmed-interest',
          hidden: !isRecruiterGbs,
        },
        {
          defaultCaption: 'Rating',
          toggledCaption: 'Rating',
          value: 'rating',
        },
        {
          defaultCaption: 'GAT Score',
          toggledCaption: 'GAT Score',
          value: 'gat',
        },
        {
          defaultCaption: 'ALGO',
          toggledCaption: 'ALGO',
          value: 'algo',
          hidden: type !== 'process',
        },
      ],
    ];
  }, [id]);

  const handleOnSelectAll = checked => {
    checked ? selectAll() : deselectAll();
  };

  const sendMessage = ({ CandidateID, FullName }) => {
    message(CandidateID, FullName, MESSAGE_TYPE.TYPE_RECRUITER_SEARCH_OTHER);
  };

  const handleRatedCandidate = item => {
    takeAction
      .rate(item, id)
      .then(() => grid.actions.fetch())
      .catch(() => {});
  };

  const sendMassMessage = () => {
    messageSelected(getSelectedIds(), MESSAGE_TYPE.TYPE_RECRUITER_SEARCH_OTHER);
  };

  const handleDownloadResume = item => {
    takeAction.resume(item);
  };

  const handleDownloadCoverLetter = item => {
    get(
        ENDPOINT.ACTION.GET_COVER_LETTER_LINK + `/id/${id}`,
        {candidateId: item.CandidateID},
        {
          handleError: () => {
            BasicDialog({
              bodyRender: 'File not found',
            })
          },
          handleSuccess: ({ link }) => {
            FileHelper.downloadInitByUrl(link);
          }
        }
    );
  };

  const handleChangeStatus = candidate => {
    takeAction.status(id, candidate).then(() => grid.actions.fetch());
  };

  const handleSendLinks = () => {
    sendLinksEmail(id, getSelectedIds());
  };

  const handleSendGatEmail = () => {
    sendGatEmail(id, getSelectedIds());
  };

  const handleSendScheduleEmail = () => {
    sendScheduleEmail(id, getSelectedIds());
  };

  const handleDownloadExcel = () => {
    downloadExcelApplicants(id, getSelectedIds(), filters.actions.getQuery());
  };

  const handleMassDownloadResume = () => {
    downloadResumeApplicants(id, getSelectedIds(), filters.actions.getQuery());
  };

  const addShortlistHandler = () => {
    addToShortlist(getSelectedIds(), isRecruiterPremiumRecFirm);
  };

  const handleMassChangeStatus = () => {
    setMassStatus(id, getSelectedIds()).then(() => grid.actions.fetch());
  };

  const handleMassSendJob = () => {
    const ids = getSelectedIds();

    if (!ids || !ids.length) {
      BasicDialog({
        bodyRender: 'Please select at least one candidate',
        bodyStyles: POPUP_STYLES.warningBodyStyles,
        popupStyles: POPUP_STYLES.warningPopupStyles,
        popupInnerStyles: POPUP_STYLES.warningContentStyles,
        withoutButtons: true,
      });
      return;
    } else if (ids.length > 500) {
      BasicDialog({
        bodyRender:
          'You cannot send Setup Email to more than 500 candidates at once',
        bodyStyles: POPUP_STYLES.warningBodyStyles,
        popupStyles: POPUP_STYLES.warningPopupStyles,
        popupInnerStyles: POPUP_STYLES.warningContentStyles,
        withoutButtons: true,
      });
      return;
    }

    checkMassSendJob().then(
      ({ diff, clear, clearNotifyOff, dirty, dirtyNotifyOff, id, ids }) => {
        MassSendJobDialog({
          params: { type, diff, clear, clearNotifyOff, dirty, dirtyNotifyOff },
        }).then(result => {
          setSendJobButtonDisabled(true);
          loaderShow();
          api.post(
            ENDPOINT.ACTION.CANDIDATE_SEND_JOB,
            { id, ids, ...result },
            {
              handleError: response => {
                ResponseErrorDialog({ response });
                setSendJobButtonDisabled(false);
              },
              handleSuccess: () => {
                grid.actions.fetch();
                setSendJobButtonDisabled(false);
              },
              handleComplete: () => loaderHide(),
            }
          );
        });
      }
    );
  };

  const checkMassSendJob = async () => {
    const params = { id, ids: getSelectedIds().join(',') };

    return new Promise(resolve => {
      setSendJobButtonDisabled(true);
      api.get(ENDPOINT.ACTION.CANDIDATE_SEND_JOB_COUNT, params, {
        handleError: response => {
          setSendJobButtonDisabled(false);
          const { code = null } = response.data;
          if (code && code === ERROR_CODE.NO_RECIPIENTS) {
            BasicDialog({
              bodyRender: (
                <div className="send-job--warning-message">
                  Selected candidates cannot receive Setup Email for one of the
                  following reasons:
                  <ul>
                    <li>
                      Candidate has already received and responded to Setup
                      Email
                    </li>
                    <li>Candidate is a recruiter</li>
                  </ul>
                </div>
              ),
              bodyStyles: POPUP_STYLES.warningBodyStyles,
              popupStyles: {
                minHeight: '20vh',
                marginTop: '25px',
                padding: '35px 25px',
                maxWidth: '620px',
              },
              popupInnerStyles: POPUP_STYLES.warningContentStyles,
              withoutButtons: true,
            });
          } else {
            ResponseErrorDialog({ response });
          }
        },
        handleSuccess: responseData => {
          setSendJobButtonDisabled(false);
          resolve({ ...responseData, ...params });
        },
      });
    });
  };

  const searchActionButtons = [
    {
      caption: 'Send Job',
      controlFunc: handleMassSendJob,
      disabled: isSendJobButtonDisabled,
      hidden: !appData.HasSetupEmail,
    },
    {
      caption: 'Message',
      controlFunc: sendMassMessage,
    },
    {
      caption: 'Shortlist',
      controlFunc: addShortlistHandler,
      hidden: !isAllowedCandidatesShortlists,
    },
    {
      caption: 'Links',
      controlFunc: handleSendLinks,
      hidden: !isRecruiterPremiumRecFirm,
    },
    {
      caption: 'Status',
      controlFunc: handleMassChangeStatus,
      hidden:
        appData.IsSubscriptionProcess ||
        !isAllowedChangeMultipleCandidatesStatus,
    },
    {
      caption: 'Resumes',
      controlFunc: handleMassDownloadResume,
    },
    {
      caption: 'Excel',
      controlFunc: handleDownloadExcel,
    },
    {
      caption: 'GAT',
      controlFunc: handleSendGatEmail,
    },
    {
      caption: 'Schedule',
      controlFunc: handleSendScheduleEmail,
      hidden: appData.IsSubscriptionProcess || !isAllowedPersonalCalendar,
    },
  ];

  const searchActionSelectors = [
    {
      caption: 'Select All',
      icon: 'tick',
      iconActive: 'cross',
      controlFunc: handleOnSelectAll,
    },
  ];

  const itemFooterActions = [
    {
      caption: appData.CoverLetterRequire ? 'Resume' : 'Download Resume',
      cls: appData.CoverLetterRequire ? 'item-footer-action__min-width-ignore' : '',
      icon: 'download',
      handler: handleDownloadResume,
    },
    {
      caption: 'Letter',
      hidden: !appData.CoverLetterRequire,
      icon: 'download',
      handler: handleDownloadCoverLetter,
    },
    {
      caption: 'Message',
      icon: 'message',
      handler: sendMessage,
    },
    {
      caption: 'Rate',
      icon: 'rate',
      handler: handleRatedCandidate,
    },
    {
      caption: 'Tag',
      icon: 'tag',
      handler: takeAction.tag,
      hidden: !Role.isRecruiterPremiumRecFirm(user),
    },
    {
      caption: 'Status',
      icon: 'status',
      handler: handleChangeStatus,
    },
  ];

  return (
    <GridHistoryWithFilters
      history={history}
      grid={grid}
      filters={filters}
      initialParams={{ sortBy: 'default' }}
      matchParams={{ id }}
      filtersParams={{ id }}
    >
      <PageLayout
        pageClass="view-applicants-page"
        pageTitle="Manage Process"
        mainContent={
          <>
            {grid.data.init ? (
              <ListSearch
                sortBy={grid.data.params.sortBy || ''}
                sortControls={sortControls}
                recordsCount={grid.data.rowsCount}
                recordsName={
                  Number(grid.data.rowsCount) === 1 ? 'match' : 'matches'
                }
                search={grid.data.params.search || false}
                searchPlaceholder="Type your search here"
                onChangeSortByHandler={grid.actions.sortBy}
                onSendSearchQueryHandler={grid.actions.search}
                actionButtonControls={searchActionButtons}
                actionSelectControls={searchActionSelectors}
                isFetching={grid.data.isFetching}
                renderHeader={
                  <div className="shortlist-professionals-page__header">
                    {appData.PositionTitle && (
                      <>
                        <h3 className="shortlist-professionals-page__sub-title">
                          <>
                            <NumberFormat
                              thousandSeparator
                              value={appData.ApplicantsCount}
                              displayType={'text'}
                            />{' '}
                            Application
                            {Number(appData.ApplicantsCount) === 1
                              ? ``
                              : `s`}{' '}
                            Received
                          </>
                        </h3>
                        <h2 className="shortlist-professionals-page__title">
                          {appData.PositionTitle}
                        </h2>
                      </>
                    )}
                    <MediaQuery query="(max-width: 767px)">
                      <FiltersMobileToggler
                        title="Search Filters:"
                        toggleFunc={toggleMobileFiltersHandler}
                      />
                    </MediaQuery>
                  </div>
                }
              />
            ) : (
              <ListSearchContentLoader />
            )}
            <div className="horizontal-loader__container">
              {grid.data.isFetching && <HorizontalLoader />}
            </div>

            {grid.data.init && (
              <>
                <List
                  items={grid.data.rows}
                  itemRender={(item, actions) => (
                    <CandidateItem
                      key={item.CandidateID}
                      hideImmediateStart
                      user={user}
                      item={item}
                      actions={actions}
                      selected={isSelected(item.CandidateID)}
                      footerActions={itemFooterActions}
                      showDateApply={true}
                    />
                  )}
                  selectable
                  actions={{
                    onSelect: select,
                    onDeselect: deselect,
                    onChangePage: grid.actions.readPage,
                  }}
                  pagination={{
                    page: grid.data.page,
                    count: grid.data.rowsCount,
                    perPage: grid.data.perPage,
                  }}
                />
              </>
            )}
          </>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              {grid.data.isFetching && (
                <CSSTransition
                  in={grid.data.init}
                  appear={true}
                  timeout={ANIMATION_TIMEOUT_APPEAR}
                  classNames="fade"
                >
                  <NoEventsMask relative={true} />
                </CSSTransition>
              )}
              <WidgetOnMobile
                renderComponent={filtersAccordion}
                status={mobileFilters}
                toggleFunc={toggleMobileFiltersHandler}
              />
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistoryWithFilters>
  );
};

export default compose(withSearchActions, withTakeActions)(ViewApplicants);
